import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { authService } from '@/services/api';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/contexts/ToastContext';
import { ROUTES } from '@/constants';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Supabase v2 delivers the recovery token in the URL hash fragment
  // (#access_token=...&type=recovery&expires_in=...), not the query string.
  // supabase-js parses it on load and fires an auth state change, so a valid
  // recovery session should already exist by the time we mount.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const token_hash = searchParams.get('token_hash');
      const type = searchParams.get('type');

      const { data: { session } } = await supabase.auth.getSession();
      const hasSession = !!session;
      const hasToken = token_hash && type === 'recovery';

      if (!cancelled) {
        if (!hasSession && !hasToken) {
          setInvalid(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  if (invalid) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md glass-panel rounded-3xl p-8 shadow-2xl text-center"
        >
          <div className="h-12 w-12 rounded-full bg-red-500 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            Invalid or expired reset link
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 mt-2">
            Request a new password reset on the sign-in page.
          </p>
          <Button
            onClick={() => navigate(ROUTES.LOGIN)}
            className="mt-6 w-full"
          >
            Back to sign in
          </Button>
        </motion.div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast('Passwords do not match', '', 'destructive');
      return;
    }
    setLoading(true);
    const { error } = await authService.updatePassword(password);
    if (error) {
      toast('Failed', error.message, 'destructive');
    } else {
      setComplete(true);
    }
    setLoading(false);
  };

  if (complete) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md glass-panel rounded-3xl p-8 shadow-2xl text-center"
        >
          <div className="h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            Password reset
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 mt-2">
            Your password has been changed successfully.
          </p>
          <Button onClick={() => navigate(ROUTES.LOGIN)} className="mt-6 w-full">
            Sign in
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md glass-panel rounded-3xl p-8 shadow-2xl"
      >
        <div className="text-center mb-8">
          <div className="h-12 w-12 rounded-2xl bg-brand-600 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
            K
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            Reset password
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 mt-2">
            Create a new password
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
          />
          <Input
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
          />
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset password'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to={ROUTES.LOGIN} className="text-sm text-neutral-600 dark:text-neutral-400 hover:underline">
            ← Back to sign in
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
